import { useCompetitorControllerGetCompetitorRelation } from "@/api/types/main";
import { MarketPlace } from "@/api/types/main/schemas";
import { CompetitorMarked } from "@/components/custom-ui/competitor.marked";
import { Detail } from "@/components/custom-ui/detail";
import { LoadingSpinner } from "@/components/custom-ui/loading.spinner";
import { MetricCell } from "@/components/custom-ui/metric.cell";
import NumeralDataWidget from "@/components/custom-ui/widgets/numeral.data.period.widget";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { getMarketplaceIcons } from "@/lib/utils";
import { CompetitorsLayoutOutletContext } from "@/pages/shared/services/competitors/layout";
import { useOutletContext, useParams } from "react-router-dom";

function parseRouteNumber(value: string | undefined) {
  if (!value) return null;

  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : null;
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="flex h-full items-center justify-center p-4 text-center font-medium text-destructive">
      {message}
    </div>
  );
}

function EmptyValue() {
  return <span className="text-muted-foreground">-</span>;
}

type DetailItem = {
  label: string;
  value: React.ReactNode;
};

function DetailsGrid({ items }: { items: DetailItem[] }) {
  return (
    <div className="grid gap-x-6 gap-y-3 md:grid-cols-2 xl:grid-cols-3">
      {items.map((item) => (
        <Detail key={item.label} label={item.label} value={item.value} />
      ))}
    </div>
  );
}

export function CompetitorLayout() {
  const { sku, competitorId } = useParams<{
    sku: string;
    competitorId: string;
  }>();

  const { date } = useOutletContext<CompetitorsLayoutOutletContext>();

  const skuNumber = parseRouteNumber(sku);
  const competitorSkuNumber = parseRouteNumber(competitorId);

  const canFetch = Boolean(date && skuNumber && competitorSkuNumber);

  const {
    data: competitorRelation,
    isLoading: isCompetitorRelationLoading,
    isError: isCompetitorRelationError,
    error: competitorRelationError,
  } = useCompetitorControllerGetCompetitorRelation(
    skuNumber ?? 0,
    competitorSkuNumber ?? 0,
    { date },
    {
      query: {
        enabled: canFetch,
      },
    }
  );

  if (!skuNumber || !competitorSkuNumber) {
    return <ErrorState message="Некорректные параметры SKU в адресной строке" />;
  }

  if (!date) {
    return <ErrorState message="Не выбрана дата для загрузки данных" />;
  }

  if (isCompetitorRelationLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (isCompetitorRelationError) {
    return (
      <ErrorState message={`Ошибка при загрузке данных: ${competitorRelationError?.toString()}`} />
    );
  }

  if (!competitorRelation) {
    return <ErrorState message="Данные по конкуренту не найдены" />;
  }

  const competitorName = competitorRelation.name || "Конкурент без названия";

  const baseDetails: DetailItem[] = [
    {
      label: "Цвет",
      value: competitorRelation.color,
    },
    {
      label: "Остатки",
      value: <MetricCell value={competitorRelation.balance} unit="pieces" />,
    },
    {
      label: "Финальная цена",
      value: <MetricCell value={competitorRelation.final_price} unit="rub" />,
    },
    {
      label: "Средняя финальная цена за месяц",
      value: <MetricCell value={competitorRelation.final_price_average} unit="rub" />,
    },
    {
      label: "Медианная финальная цена за месяц",
      value: <MetricCell value={competitorRelation.final_price_median} unit="rub" />,
    },
    {
      label: "Базовая скидка",
      value: <MetricCell value={competitorRelation.basic_sale} unit="percent" />,
    },
    {
      label: "Базовая цена",
      value: <MetricCell value={competitorRelation.basic_price} unit="rub" />,
    },
    {
      label: "Промо скидка",
      value: <MetricCell value={competitorRelation.promo_sale} unit="percent" />,
    },
    {
      label: "Клиентская скидка",
      value: <MetricCell value={competitorRelation.client_sale} unit="percent" />,
    },
    {
      label: "Клиентская цена",
      value: <MetricCell value={competitorRelation.client_price} unit="rub" />,
    },
    {
      label: "Стартовая цена",
      value: <MetricCell value={competitorRelation.start_price} unit="rub" />,
    },
    {
      label: "Цена с кошельком",
      value: <MetricCell value={competitorRelation.wallet_price} unit="rub" />,
    },
    {
      label: "Заказы",
      value: <MetricCell value={competitorRelation.orders} unit="pieces" />,
    },
    {
      label: "Рейтинг средний за месяц",
      value: <MetricCell value={competitorRelation.rating} />,
    },
    {
      label: "Комментарии за месяц",
      value: <MetricCell value={competitorRelation.comments} unit="pieces" />,
    },
    {
      label: "Средние продажи в день",
      value: <MetricCell value={competitorRelation.sales_per_day_average} unit="pieces" />,
    },
    {
      label: "Потенциальная выручка",
      value: <MetricCell value={competitorRelation.revenue_potential} unit="rub" />,
    },
    {
      label: "Средняя выручка",
      value: <MetricCell value={competitorRelation.revenue_average} unit="rub" />,
    },
    {
      label: "Упущенная прибыль",
      value: <MetricCell value={competitorRelation.lost_profit} unit="rub" />,
    },
    {
      label: "Процент упущенной прибыли",
      value: <MetricCell value={competitorRelation.lost_profit_percent} unit="percent" />,
    },
    {
      label: "Дней с продажами",
      value: <MetricCell value={competitorRelation.days_with_sales} />,
    },
    {
      label: "Среднее при наличии",
      value: <MetricCell value={competitorRelation.average_if_in_stock} />,
    },
    {
      label: "Предмет",
      value: competitorRelation.subject,
    },
    {
      label: "Первая дата SKU на сайте",
      value: competitorRelation.sku_first_date,
    },
    {
      label: "Дата первого комментария на сайте",
      value: competitorRelation.firstcommentdate,
    },
    {
      label: "Категория",
      value: competitorRelation.category,
    },
    {
      label: "Дни оборачиваемости",
      value: <MetricCell value={competitorRelation.turnover_days} />,
    },
    {
      label: "Замороженные остатки",
      value: <MetricCell value={competitorRelation.frozen_stocks} unit="pieces" />,
    },
    {
      label: "Стоимость замороженных остатков",
      value: <MetricCell value={competitorRelation.frozen_stocks_cost} unit="rub" />,
    },
    {
      label: "Процент замороженных остатков",
      value: <MetricCell value={competitorRelation.frozen_stocks_percent} unit="percent" />,
    },
    {
      label: "Количество похожих продуктов",
      value: <MetricCell value={competitorRelation.related_products_count} unit="pieces" />,
    },
    {
      label: "Количество похожих продуктов в наличии",
      value: (
        <MetricCell value={competitorRelation.related_products_in_stock_count} unit="pieces" />
      ),
    },
    {
      label: "Средний CPM в поиске",
      value: <MetricCell value={competitorRelation.search_cpm_avg} unit="rub" />,
    },
    {
      label: "Длина упаковки",
      value: <MetricCell value={competitorRelation.package_length} />,
    },
    {
      label: "Ширина упаковки",
      value: <MetricCell value={competitorRelation.package_width} />,
    },
    {
      label: "Высота упаковки",
      value: <MetricCell value={competitorRelation.package_height} />,
    },
  ];

  const economyDetails: DetailItem[] = [
    {
      label: "SKU конкурента",
      value: competitorRelation.competitorSku,
    },
    {
      label: "Бренд конкурента",
      value: competitorRelation.brand,
    },
    {
      label: "Продавец",
      value: competitorRelation.seller,
    },
    {
      label: "Выручка конкурента",
      value: <MetricCell value={competitorRelation.revenue} unit="rub" />,
    },
    {
      label: "Заказы",
      value: <MetricCell value={competitorRelation.orders} unit="pieces" />,
    },
    {
      label: "Процент от выручки в категории",
      value: <MetricCell value={competitorRelation.revenue_share} unit="percent" />,
    },
    {
      label: "Средний чек конкурента",
      value: <MetricCell value={competitorRelation.avg_price} unit="rub" />,
    },
    {
      label: "Рейтинг конкурента",
      value: <MetricCell value={competitorRelation.rating} />,
    },
    {
      label: "Количество отзывов конкурента",
      value: <MetricCell value={competitorRelation.comments} unit="pieces" />,
    },
    {
      label: "Дней в наличии у конкурента",
      value: <MetricCell value={competitorRelation.days_with_sales} />,
    },
    {
      label: "Позиция в категории",
      value: <MetricCell value={competitorRelation.category_position} />,
    },
    {
      label: "Средняя позиция рекламы в поиске",
      value: <MetricCell value={competitorRelation.search_ad_position_avg} />,
    },
    {
      label: "Средняя органическая позиция в поиске",
      value: <MetricCell value={competitorRelation.search_organic_position_avg} />,
    },
  ];

  return (
    <div className="h-full min-h-0 overflow-hidden p-2">
      <ScrollArea className="h-full min-h-0">
        <div className="flex min-h-full flex-col gap-4 pr-3">
          <Card>
            <CardContent className="p-4">
              <div className="grid gap-3 md:grid-cols-[auto_minmax(0,1fr)_auto] md:items-center">
                <div className="flex items-center gap-2">
                  {competitorRelation.url ? (
                    <a
                      href={competitorRelation.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Открыть товар на маркетплейсе"
                      className="rounded-md p-2 transition-colors hover:bg-accent"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {getMarketplaceIcons(MarketPlace.WB)}
                    </a>
                  ) : (
                    <div className="rounded-md p-2 opacity-50">
                      {getMarketplaceIcons(MarketPlace.WB)}
                    </div>
                  )}
                </div>

                <div className="min-w-0">
                  <h1 className="truncate text-lg font-semibold">{competitorName}</h1>
                  <div className="truncate text-sm text-muted-foreground">
                    SKU конкурента: {competitorId}
                  </div>
                </div>

                <div className="flex justify-start md:justify-end">
                  <CompetitorMarked
                    isMarked={competitorRelation.isMarked ?? false}
                    sku={skuNumber}
                    competitorSku={competitorSkuNumber}
                    date={date}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <section className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Продавец</CardTitle>
              </CardHeader>

              <CardContent className="flex flex-col items-stretch justify-evenly h-full">
                <Detail label="Бренд" value={competitorRelation.brand} />
                <Detail label="Продавец" value={competitorRelation.seller} />
              </CardContent>
            </Card>

            <Card className="min-w-0">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Объяснение связи с конкурентом</CardTitle>
              </CardHeader>

              <CardContent>
                <ScrollArea className="h-32 rounded-md border bg-muted/20">
                  <p className="whitespace-pre-wrap break-words p-3 text-sm leading-relaxed text-muted-foreground">
                    {competitorRelation.explanation || "Нет объяснения"}
                  </p>

                  <ScrollBar orientation="vertical" />
                </ScrollArea>
              </CardContent>
            </Card>
          </section>

          <section className="grid gap-2 md:grid-cols-3">
            <NumeralDataWidget
              value={competitorRelation.rating ?? 0}
              title="Рейтинг конкурента"
              description="Рейтинг конкурента"
              valueTag="none"
            />

            <NumeralDataWidget
              value={competitorRelation.comments ?? 0}
              title="Комментарии конкурента"
              description="Комментарии конкурента"
              valueTag="count"
            />

            <NumeralDataWidget
              value={competitorRelation.category_position ?? 0}
              title="Позиция в категории"
              description="Позиция конкурента в категории"
              valueTag="none"
            />
          </section>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Особенности товара конкурента</CardTitle>
            </CardHeader>

            <CardContent>
              <div className="whitespace-pre-wrap break-words text-sm leading-relaxed">
                {competitorRelation.greatFeatures || <EmptyValue />}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Базовые характеристики</CardTitle>
            </CardHeader>

            <CardContent>
              <DetailsGrid items={baseDetails} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Экономические характеристики</CardTitle>
            </CardHeader>

            <CardContent>
              <DetailsGrid items={economyDetails} />
            </CardContent>
          </Card>
        </div>

        <ScrollBar orientation="vertical" />
      </ScrollArea>
    </div>
  );
}
